<!--
  One student, the same card for everyone who can see them: Miles on Students, and a tutor for
  the students assigned to her. What shows is up to the caller: a tutor gets the name, class,
  who teaches them and the lesson bar, never rates, the family or what they pay.
-->
<script lang="ts">
  import type { Snippet } from "svelte";
  import { hoursText, type LessonProgress as Progress } from "@split/core";
  import LessonProgress from "./LessonProgress.svelte";
  import { formatCents } from "../../lib/format.ts";

  let {
    name,
    family = "",
    badge = "",
    badgeTitle = "",
    parts,
    prog = null,
    usedNoPrepaidMinutes = 0,
    balanceCents = null,
    sessions = null,
    expanded = false,
    ontoggle,
    children,
  }: {
    name: string;
    family?: string;
    badge?: string;
    badgeTitle?: string;
    /** The line under the name, joined with " · " (empty parts are left out). */
    parts: string[];
    /** The lesson bar, when they have prepaid lessons. */
    prog?: Progress | null;
    usedNoPrepaidMinutes?: number;
    /** What the family owes (null: not shown, as for a tutor). */
    balanceCents?: number | null;
    /** Lessons taught, for students with nothing prepaid (tutor's view). */
    sessions?: number | null;
    expanded?: boolean;
    ontoggle?: () => void;
    children?: Snippet;
  } = $props();
</script>

<div class="row">
  <button class="main" onclick={() => ontoggle?.()} aria-expanded={children ? expanded : undefined} disabled={!ontoggle}>
    <span class="who">
      <span class="name">
        {name}
        {#if family}<span class="muted fam">{family}</span>{/if}
        {#if badge}<span class="gold" title={badgeTitle}>★ {badge}</span>{/if}
      </span>
      <span class="line">{parts.filter(Boolean).join(" · ")}</span>
    </span>
    <span class="stat">
      {#if prog && prog.prepaidMinutes}
        <LessonProgress p={prog} />
      {:else if usedNoPrepaidMinutes}
        <span class="owes">{hoursText(usedNoPrepaidMinutes)} used, none prepaid yet</span>
      {:else if sessions !== null}
        <span class="muted">{sessions} {sessions === 1 ? "lesson" : "lessons"}</span>
      {/if}
      {#if balanceCents !== null}
        <span class:owes={balanceCents > 0}>{balanceCents > 0 ? `${formatCents(balanceCents)} owed` : balanceCents < 0 ? `${formatCents(-balanceCents)} credit` : "Paid up"}</span>
      {/if}
    </span>
  </button>
  {#if expanded && children}{@render children()}{/if}
</div>

<style>
  .row {
    background: var(--surface);
  }

  .main {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    padding: 14px 16px;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }

  .main:disabled {
    cursor: default;
  }

  .who {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  .name {
    font-weight: 650;
    font-size: var(--t-lg);
  }

  .fam {
    font-weight: 500;
    font-size: var(--t-sm);
    margin-left: 6px;
  }

  .gold {
    margin-left: 6px;
    padding: 1px 8px;
    border-radius: 999px;
    background: linear-gradient(135deg, #f7d774, #d9a514);
    color: #4a3500;
    font-size: var(--t-xs);
    font-weight: 700;
    vertical-align: middle;
  }

  .line {
    color: var(--ink-2);
    font-size: var(--t-sm);
  }

  .stat {
    display: grid;
    justify-items: end;
    gap: 6px;
    font-size: var(--t-sm);
    white-space: nowrap;
  }

  /* Phone: the bar and balance go under the name instead of squeezing it. */
  @media (max-width: 640px) {
    .main {
      flex-wrap: wrap;
    }

    .stat {
      justify-items: start;
      width: 100%;
    }
  }
</style>
